import { useMutation, useQuery } from "@apollo/client";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { Form, FormInstance } from "antd";
import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import globals from "../../Constants/Globals";
import { RoleCodes } from "../../Constants/RoleCode";
import { CREATE_USER, UPDATE_USER } from "../../Graphql/Mutations";
import { Error } from "../../Types/MessageType";
import { permissionValuesArr } from "../../Types/SelectType";
import FormItemInput from "../FormItems/FormItemInput";
import FormItemSelect from "../FormItems/FormItemSelect";
import { useLazyQuery } from "@apollo/client";
import { GET_ALL_ORGANIZATIONS, GET_USER_BY_ID, GET_COHORT_BY_ID, GET_BATCHES_BY_ORG_ID } from '../../Graphql/Queries';
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { SuperAdminPermissions } from "../../Constants/Permissions";
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { useWatch } from "antd/es/form/Form";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';

type createUserProp = {
  disabledEv: (val: boolean) => void;
  alertMessage: (val: Error) => void;
};

const options: permissionValuesArr[] = [
  {
    label: 'Company Admin',
    value: RoleCodes.COMPANY_ADMIN,
    permission: SuperAdminPermissions.CREATE_USER_COMPANY_ADMIN
  },
  {
    label: 'Company Manager',
    value: RoleCodes.COMPANY_MANAGER,
    permission: SuperAdminPermissions.CREATE_USER_COMPANY_MANAGER
  },
  {
    label: 'Company User',
    value: RoleCodes.COMPANY_USER,
    permission: SuperAdminPermissions.CREATE_USER_COMPANY_USER
  },
  {
    label: 'Batch Admin',
    value: RoleCodes.BATCH_ADMIN,
    permission: SuperAdminPermissions.CREATE_USER_BATCH_ADMIN
  }
];


const CreateUserForm = ({ disabledEv, alertMessage }: createUserProp, ref: any) => {
  const [form] = Form.useForm();
  const submitRef = useRef<FormInstance<any>>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const { organization, permissions } = useSelector((state: any) => state.auth);
  const { userId } = useSelector((state: any) => state.auth);
  const [fetchLoggedInUser, { data: loggedInUserData }] = useLazyQuery(GET_USER_BY_ID);
  const [userDataState, setUserData] = useState<any>({});
  const [createUser, { loading: creatingUser }] = useMutation(CREATE_USER);
  const [updateUserHandler, { loading: updateUser }] = useMutation(UPDATE_USER);
  const orgId = useWatch('organization', form);
  const [batchesOptions, setBatchesOptions] = useState<permissionValuesArr[]>([]);
  const [selectedBatches, setSelectedBatches] = useState<string[]>([]);
  const [isAccessCodeVisible, setIsAccessCodeVisible] = useState(false); // State to toggle visibility
  const [isCompanyIdentifierVisible, setIsCompanyIdentifierVisible] = useState(false);

  const [getBatchesByOrgId] = useLazyQuery(GET_BATCHES_BY_ORG_ID, {
    onCompleted(data) {
      const options = data?.getBatchesByOrgId?.map((batch: { _id: string; cohortName: string }) => ({
        label: batch.cohortName,
        value: batch._id,
        permission: ''
      })) || [];
      setBatchesOptions(options);
    }
  });

  const [getBatchDetails] = useLazyQuery(GET_COHORT_BY_ID);
  const [isBatchAdmin, setIsBatchAdmin] = useState(false);

  useEffect(() => {
    if (loggedInUserData?.userWithRolesById?.roles) {
      const isBatchAdminRole = loggedInUserData.userWithRolesById.roles.some(
        (role: any) => role.roleCode === RoleCodes.BATCH_ADMIN
      );
      setIsBatchAdmin(isBatchAdminRole);
    } else {
      console.log("Logged in user roles are not yet available or user has no roles");
    }
  }, [loggedInUserData]);

  useEffect(() => {
    if (userId) {
      fetchLoggedInUser({ variables: { userWithRolesByIdId: userId } });
    }
  }, [userId]);

  // Effect to handle initial load and navigation
  useEffect(() => {
    const fetchUserAndBatches = async () => {
      if (location.state?.userId) {
        // Get user details
        const userResult = await getUserById({
          variables: {
            userWithRolesByIdId: location.state.userId
          }
        });

        if (userResult.data?.userWithRolesById) {
          const userData = userResult.data.userWithRolesById;

          // Set form values
          form.setFieldsValue({
            name: userData.name,
            email: userData.email,
            gender: userData.gender,
            phone: userData.phone,
            roleCode: userData.roles[0].roleName,
            organization: userData.organization,
            batches: userData.batchIds || []
          });

          setSelectedBatches(userData.batchIds || []);

          // First fetch all batches for the organization
          if (userData.organization) {
            await getBatchesByOrgId({
              variables: { orgId: userData.organization }
            });
          }

          // Then fetch details for user's specific batches
          if (userData.batchIds?.length) {
            for (const batchId of userData.batchIds) {
              const batchResult = await getBatchDetails({
                variables: { id: batchId }
              });

              if (batchResult.data?.getCohortById) {
                const batch = batchResult.data.getCohortById;
                setBatchesOptions(prevOptions => {
                  if (!prevOptions.some(opt => opt.value === batch._id)) {
                    return [...prevOptions, {
                      label: batch.cohortName,
                      value: batch._id,
                      permission: ''
                    } as permissionValuesArr];
                  }
                  return prevOptions;
                });
              }
            }
          }
        }
      }
    };

    fetchUserAndBatches();
  }, [location.state?.userId]);

  // Effect to handle organization change
  useEffect(() => {
    const orgId = form.getFieldValue('organization');
    if (orgId) {
      getBatchesByOrgId({ variables: { orgId } });
    }
  }, [form.getFieldValue('organization')]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      setBatchesOptions([]);
      setSelectedBatches([]);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    inputRef: submitRef,
    submit: () => form.submit(),
  }));

  useImperativeHandle(ref, () => ({
    inputRef: inputRef,
    submit: () => form.submit()
  }));

  const { data } = useQuery<any>(GET_ALL_ORGANIZATIONS, {
    variables: {
      page: 1,
      limit: 30
    },
    skip: !permissions.includes(SuperAdminPermissions.VIEW_ORGANIZATION)
  })

  const updatedOptions: permissionValuesArr[] = options.filter((option) => permissions.includes(option.permission))

  let organizationsOptions: { label: any; value: any; }[] = [];

  data?.allOrganizations?.organizations.map((org: { name: any; _id: any; }) => {
    organizationsOptions.push({
      label: org.name,
      value: org._id
    })
  });

  const [getUserById] = useLazyQuery(GET_USER_BY_ID, {
    onCompleted(data) {
      if (!!data.userWithRolesById) {
        const userData = data.userWithRolesById;
        form.setFieldsValue({
          ...userData,
          batches: userData.batchIds || [],
        });
        setSelectedBatches(userData.batchIds || []);
        setUserData((fetchedData: any) => ({
          ...fetchedData,
          oldRoleCode: userData.roles[0].roleCode,
        }));

        // Fetch batches for the user's organization
        if (userData.organization) {
          getBatchesByOrgId({ variables: { orgId: userData.organization } });
        }
      }
    }
  });

  const createUserHandler = () => {
    const userData = form.getFieldsValue();
    delete userData['userCode'];
    delete userData['companyIdentifier']; 

    if (!!location.state?.userId) {
      delete userData['roleCode'];
      updateUserHandler({
        variables: {
          userInput: {
            ...userDataState,
            ...userData,
            newRoleCode: form.getFieldValue('roleCode'),
            _id: location.state?.userId,
            organization: data ? form.getFieldValue('organization') : organization,
          }
        },
        onCompleted() {
          alertMessage({ type: "success", message: "User updated successfully" });
          form.resetFields();
          setTimeout(() => {
            alertMessage({
              type: 'success',
              message: ''
            });
          }, globals.timeout);
        },
        onError(error) {
          alertMessage({ type: 'error', message: error.message });
          setTimeout(() => {
            alertMessage({
              type: 'success',
              message: ''
            });
          }, globals.timeout);
        }
      });
    } else {
      createUser({
        variables: {
          userInput: {
            ...userData,
            organization: data ? form.getFieldValue('organization') : organization,
          }
        },
        onCompleted() {
          alertMessage({ type: "success", message: "User created successfully" });
          form.resetFields();
          setTimeout(() => {
            alertMessage({
              type: 'success',
              message: ''
            });
          }, globals.timeout);
        },
        onError(error) {
          alertMessage({ type: 'error', message: error.message });
          setTimeout(() => {
            alertMessage({
              type: 'error',
              message: ''
            });
          }, globals.timeout);
        }
      });
    }
  };

  useEffect(() => {
    disabledEv(location.state?.userId ? updateUser : creatingUser);
  }, [creatingUser, location.state?.userId, updateUser, disabledEv]);

  const [phone, setPhone] = useState("");

  const extractLocalNumber = (phone: string | null | undefined) => {
    if (!phone) {
      return "";
    }
    // Remove any non-digit characters and spaces
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length > 10) {
      return digitsOnly.slice(-10);
    }
    return digitsOnly;
  };

  const validatePhoneNumber = (phone: string | null | undefined) => {
    if (!phone) {
      return false;
    }
    const localNumber = extractLocalNumber(phone);
    return localNumber.length === 10;
  };

  const handleValue = (value: string | null | undefined) => {
    setPhone(value || "");
    const isValid = validatePhoneNumber(value);
    if (!isValid) {
      console.error("Phone number must be 10 digits");
    }
  };

  useEffect(() => {
    const fetchBatches = async () => {
      // Get organization ID from the form or from the logged-in user's data
      const orgIdFromForm = form.getFieldValue('organization');
      const orgIdFromUserData = loggedInUserData?.userWithRolesById?.organization;

      const orgId = orgIdFromForm || orgIdFromUserData;

      if (orgId) {
        // Fetch batches using the organization ID
        await getBatchesByOrgId({ variables: { orgId } });
      } else {
        console.warn("Organization ID is not available to fetch batches.");
      }
    };

    fetchBatches();
  }, [form.getFieldValue('organization'), loggedInUserData]);

  const toggleAccessCodeVisibility = () => {
    setIsAccessCodeVisible((prev) => !prev);
  };

  const toggleCompanyIdentifierVisibility = () => {
    setIsCompanyIdentifierVisible((prev) => !prev);
  };

  return (
    <>
      <Form
        ref={submitRef}
        onFinish={createUserHandler}
        form={form}
        layout={'vertical'}
        style={{ padding: '0 2%' }}
      >
        <Grid2 container spacing={2}>
          {/* Existing fields */}
          <Grid2 xs={12} sm={6}>
            <FormItemInput label="Name" name="name" rules={[{ required: true, message: 'Name is required' }]} type={'text'} />
          </Grid2>
          <Grid2 xs={12} sm={6}>
            <FormItemInput label="Gender" name="gender" rules={[{ required: true, message: 'Gender is required' }]} type={'radio'} />
          </Grid2>
          <Grid2 xs={12} sm={6}>
            <FormItemInput label="Email Id" name="email" rules={[{ required: true, message: 'Email is required' }, { type: 'email', message: 'Email format is incorrect' }]} type={'email'} />
          </Grid2>
          <Grid2 xs={12} sm={6}>
            <FormItemInput
              label="Phone Number"
              name="phone"
              rules={[
                {
                  required: true, message: 'Phone number is required',
                  validator: (_, value) => {
                    if (validatePhoneNumber(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject({
                      type: 'error',
                      message: 'Phone number must be 10 digits',
                      variant: 'outlined',
                    });
                  },
                },
              ]}
              type="text"
            >
              <PhoneInput
                placeholder="Enter phone number"
                value={phone}
                onChange={handleValue}
                international
              />
            </FormItemInput>
          </Grid2>
          <Grid2 xs={12} sm={6}>
            <FormItemSelect label="Role" name="roleCode" rules={[{ required: true, message: 'Role is required' }]} value={updatedOptions} setValue={(v) => null} />
          </Grid2>
          {data ? <Grid2 xs={12} sm={6}>
            <FormItemSelect label="Organization" name="organization" rules={[{ required: true, message: 'Organization is required' }]} value={organizationsOptions} setValue={(v) => null} />
          </Grid2> : null}
          {/* New Batch Dropdown */}
          <Grid2 xs={12} sm={6}>
            <FormItemSelect
              label="Batches"
              name="batches"
              rules={[{ required: true, message: "Batch is required" }]}
              mode="multiple" // Enables multiple selection
              value={batchesOptions} // Set selected batches
              setValue={(v) => null}
              disabled={isBatchAdmin}
            />
          </Grid2>
          {/* User Access Code Field */}
          <Grid2 xs={12} sm={6}>
            <Form.Item
              label="User Access Code"
              name="userCode"
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="text" // Always use text type
                  value={
                    isAccessCodeVisible
                      ? (form.getFieldValue('userCode')?.match(/\d+/)?.[0] || '')
                      : '*****'
                  }
                  readOnly
                  disabled
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                  }}
                />
                <IconButton onClick={toggleAccessCodeVisibility} sx={{ marginLeft: '8px' }}>
                  {isAccessCodeVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </div>
            </Form.Item>
          </Grid2>
          <Grid2 xs={12} sm={6}>
            <Form.Item
              label="Company Identifier"
              name="companyIdentifier"
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="text"
                  value={
                    isCompanyIdentifierVisible
                      ? (form.getFieldValue('companyIdentifier'))
                      : '*****'
                  }
                  readOnly
                  disabled
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                  }}
                />
                <IconButton onClick={toggleCompanyIdentifierVisibility} sx={{ marginLeft: '8px' }}>
                  {isCompanyIdentifierVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </div>
            </Form.Item>
          </Grid2>
        </Grid2>
      </Form>
    </>
  );
};

export default React.forwardRef(CreateUserForm);